import React, { useContext, useEffect, useMemo } from 'react'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { P } from '@frame/ui-primitives/p'

import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useCachedInfoStore } from '@frame/lib/stores/useCachedInfoStore'
import { useFileUploadStore } from '@frame/lib/stores/useFileUploadStore'
import { IncomeType } from '@frame/lib/types'
import { DataAttribute } from '@frame/types'

import { DocumentsFooter } from './components/documents-footer'
import { DragOverlay } from './components/drag-overlay'
import { ErrorAlert } from './components/error-alert'
import { Files } from './components/files'
import { UploadDropzone } from './components/upload-dropzone'
import { UploadStepContent } from './components/upload-step-content'
import { useDropzoneContext } from './contexts/dropzone-context'
import { FileUploadContext } from './contexts/file-upload-context'
import { useDocumentNavigation } from './hooks/useDocumentNavigation'
import { useDocumentSubmission } from './hooks/useDocumentSubmission'
import { useErrorHandling } from './hooks/useErrorHandling'
import { FileUploadState, IncomeSource } from './types'
import { DocumentsV2 } from './v2'

export function Documents() {
  const v2Context = useContext(FileUploadContext)

  if (v2Context) {
    return <DocumentsV2 />
  }

  return <DocumentsCurrent />
}

function DocumentsCurrent() {
  const task = useDirectTask()
  const title = useHeaderTitle()
  const showExitPrompt = useShowExitPrompt()

  const { uploadedFileIds, resetUploadedFileIds } = useFileUploadStore(({ uploadedFileIds, resetUploadedFileIds }) => ({
    uploadedFileIds,
    resetUploadedFileIds,
  }))

  const {
    dropzone: { open },
    files,
    errorMessages,
    dragActive,
    fileStatusMap,
    isUploading,
    setShowDropzone,
    removeFile,
    setErrorMessages,
    resetDropzone,
  } = useDropzoneContext()
  const { removeAllFiles } = useCachedInfoStore(({ removeAllFiles }) => ({
    removeAllFiles,
  }))

  const { submitDocuments, isSubmitting } = useDocumentSubmission()
  const { showErrors } = useErrorHandling(errorMessages)
  const { handleBack, handleNoPaystubs } = useDocumentNavigation({ task, resetDropzone })

  const { successFileCount, incomeType } = useMemo(() => {
    const uploadedFiles = Object.values(fileStatusMap).filter(
      (s) => s.state === FileUploadState.COMPLETED && files.map((f) => f.name).includes(s.fileName)
    )

    const incomeType =
      !task || task?.employer_name === IncomeSource.OTHER_INCOME ? IncomeType.OTHER : IncomeType.EMPLOYED

    return {
      successFileCount: uploadedFiles.length,
      incomeType,
    }
  }, [fileStatusMap, files, task])

  useEffect(() => {
    setShowDropzone(true)
  }, [setShowDropzone])

  async function handleSubmit() {
    submitDocuments({
      task,
      fileIds: uploadedFileIds,
      cb: () => {
        resetUploadedFileIds()
        removeAllFiles()
        resetDropzone()
      },
    })
  }

  function handleAfterErrorLeave() {
    setErrorMessages([])
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        backButton={{ onClick: handleBack }}
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.EMPLOYER}
      />
      <DragOverlay isVisible={dragActive} />
      <Body
        onClick={() => setShowDropzone(true)}
        data-cy={DataAttribute.DOCUMENTS_SCREEN_UPLOAD_STEP}
        className="relative flex h-full w-full flex-col p-5"
        paddingTopOverride={0}
      >
        <UploadStepContent incomeType={incomeType} />
        <P className="mt-2">Please check the accuracy of your documents. You won't be able to edit once you submit.</P>
        {files.length > 0 && <Files fileStatusMap={fileStatusMap} files={files} removeFile={removeFile} />}
        <UploadDropzone onClick={open} />
      </Body>
      <ErrorAlert
        showErrors={showErrors}
        errorMessages={errorMessages}
        hasFiles={files.length > 0}
        onAfterLeave={handleAfterErrorLeave}
      />
      <DocumentsFooter
        hasFiles={files.length > 0}
        successFileCount={successFileCount}
        isSubmitting={isSubmitting}
        isUploading={isUploading}
        onSubmit={handleSubmit}
        onUpload={open}
        onNoPaystubs={handleNoPaystubs}
      />
    </>
  )
}
